/**
 * Event types for the client event bus
 * These represent all possible events that can flow through the system
 */

import type { AsyncTask } from '@/stores/tasksStore.js';
import type { Schedule, ExecutionLog } from '@/stores/schedulerStore.js';

/**
 * Base event structure
 */
export interface BaseEvent {
  id: string;
  timestamp: number;
  sessionId?: string;
}

/**
 * Agent thinking event
 */
export interface ThinkingEvent extends BaseEvent {
  type: 'thinking';
}

/**
 * Streaming chunk event
 */
export interface ChunkEvent extends BaseEvent {
  type: 'chunk';
  content: string;
  chunkType: 'text' | 'reasoning';
  isComplete?: boolean;
}

/**
 * Final response event
 */
export interface ResponseEvent extends BaseEvent {
  type: 'response';
  text: string;
  reasoning?: string;
  tokenUsage?: {
    inputTokens?: number;
    outputTokens?: number;
    reasoningTokens?: number;
    totalTokens?: number;
  };
  provider?: string;
  model?: string;
  router?: string;
}

/**
 * Tool call event
 */
export interface ToolCallEvent extends BaseEvent {
  type: 'toolCall';
  toolName: string;
  toolArgs: Record<string, unknown>;
  toolCallId?: string;
}

/**
 * Tool result event
 */
export interface ToolResultEvent extends BaseEvent {
  type: 'toolResult';
  toolName: string;
  toolCallId?: string;
  success: boolean;
  result: unknown;
}

/**
 * Error event
 */
export interface ErrorEvent extends BaseEvent {
  type: 'error';
  error: string;
  recoverable?: boolean;
}

/**
 * Approval types matching webui implementation
 */
export type ApprovalType = 'tool_confirmation' | 'command_confirmation' | 'elicitation';

/**
 * Base approval event
 */
interface BaseApprovalEvent extends BaseEvent {
  type: 'approvalRequest';
  approvalId: string;
  approvalType: ApprovalType;
  metadata: Record<string, unknown>;
}

/**
 * Tool confirmation approval
 */
export interface ToolConfirmationApprovalEvent extends BaseApprovalEvent {
  approvalType: 'tool_confirmation';
  toolName: string;
  args: Record<string, unknown>;
  description?: string;
}

/**
 * Command confirmation approval
 */
export interface CommandConfirmationApprovalEvent extends BaseApprovalEvent {
  approvalType: 'command_confirmation';
  toolName: string;
  command: string;
  originalCommand?: string;
}

/**
 * Elicitation approval (ask_user)
 */
export interface ElicitationApprovalEvent extends BaseApprovalEvent {
  approvalType: 'elicitation';
  // Metadata contains: schema, prompt, serverName, context
}

/**
 * Union of all approval event types
 */
export type ApprovalRequestEvent =
  | ToolConfirmationApprovalEvent
  | CommandConfirmationApprovalEvent
  | ElicitationApprovalEvent;

/**
 * Approval resolved event (response from server)
 */
export interface ApprovalResolvedEvent extends BaseEvent {
  type: 'approvalResolved';
  approvalId: string;
  status: 'approved' | 'denied' | 'cancelled';
}

/**
 * Task lifecycle events
 */
export interface TaskCreatedEvent extends BaseEvent {
  type: 'taskCreated';
  task: AsyncTask;
}

export interface TaskUpdatedEvent extends BaseEvent {
  type: 'taskUpdated';
  task: AsyncTask;
}

export interface TaskDeletedEvent extends BaseEvent {
  type: 'taskDeleted';
  taskId: string;
}

/**
 * Scheduler events
 */
export interface ScheduleCreatedEvent extends BaseEvent {
  type: 'scheduler:scheduleCreated';
  schedule: Schedule;
}

export interface ScheduleUpdatedEvent extends BaseEvent {
  type: 'scheduler:scheduleUpdated';
  schedule: Schedule;
}

export interface ScheduleDeletedEvent extends BaseEvent {
  type: 'scheduler:scheduleDeleted';
  scheduleId: string;
  scheduleName: string;
}

export interface ScheduleTriggeredEvent extends BaseEvent {
  type: 'scheduler:scheduleTriggered';
  schedule: Schedule;
  triggeredAt: number;
  executionSessionId?: string;
}

export interface ScheduleExecutionCompletedEvent extends BaseEvent {
  type: 'scheduler:executionCompleted';
  schedule: Schedule;
  log: ExecutionLog;
}

export interface ScheduleExecutionFailedEvent extends BaseEvent {
  type: 'scheduler:executionFailed';
  schedule: Schedule;
  error: string;
  log: ExecutionLog;
}

/**
 * Session events
 */
export interface SessionTitleUpdatedEvent extends BaseEvent {
  type: 'sessionTitleUpdated';
  title: string;
}

export interface ConversationResetEvent extends BaseEvent {
  type: 'conversationReset';
}

/**
 * Connection events
 */
export interface ConnectionStatusEvent {
  type: 'connectionStatus';
  status: 'connected' | 'disconnected' | 'connecting' | 'reconnecting';
  timestamp: number;
}

/**
 * UI Action events - allows agent to control UI elements
 */
export type UIActionType =
  // New action names from backend client tools
  | 'ui_open_browser'
  | 'ui_open_notes'
  | 'ui_create_note'
  | 'ui_close_panel'
  // Legacy action names for backwards compatibility
  | 'openBrowser'
  | 'openNote'
  | 'openNotesList'
  | 'createNote'
  | 'closeArtifactPanel';

export interface UIActionEvent extends BaseEvent {
  type: 'uiAction';
  action: UIActionType | string; // Allow string for flexibility
  // New format from backend uses args
  args?: Record<string, unknown>;
  // Legacy format used payload
  payload?: {
    url?: string;
    title?: string;
    noteId?: string;
    content?: string;
  };
}

/**
 * Union of all event types
 */
export type AgentEvent =
  | ThinkingEvent
  | ChunkEvent
  | ResponseEvent
  | ToolCallEvent
  | ToolResultEvent
  | ErrorEvent
  | ToolConfirmationApprovalEvent
  | CommandConfirmationApprovalEvent
  | ElicitationApprovalEvent
  | ApprovalResolvedEvent
  | TaskCreatedEvent
  | TaskUpdatedEvent
  | TaskDeletedEvent
  | ScheduleCreatedEvent
  | ScheduleUpdatedEvent
  | ScheduleDeletedEvent
  | ScheduleTriggeredEvent
  | ScheduleExecutionCompletedEvent
  | ScheduleExecutionFailedEvent
  | SessionTitleUpdatedEvent
  | ConversationResetEvent
  | ConnectionStatusEvent
  | UIActionEvent;

/**
 * Event type names
 */
export type EventType = AgentEvent['type'];

/**
 * Middleware function type
 */
export type EventMiddleware = (event: AgentEvent) => Promise<AgentEvent> | AgentEvent;

/**
 * Event handler function type
 */
export type EventHandler<T extends AgentEvent = AgentEvent> = (event: T) => void | Promise<void>;

/**
 * Event subscription
 */
export interface EventSubscription {
  unsubscribe: () => void;
}

/**
 * Type guards for approval events
 */
export function isToolConfirmationApproval(
  event: ApprovalRequestEvent
): event is ToolConfirmationApprovalEvent {
  return event.approvalType === 'tool_confirmation';
}

export function isCommandConfirmationApproval(
  event: ApprovalRequestEvent
): event is CommandConfirmationApprovalEvent {
  return event.approvalType === 'command_confirmation';
}

export function isElicitationApproval(
  event: ApprovalRequestEvent
): event is ElicitationApprovalEvent {
  return event.approvalType === 'elicitation';
}

/**
 * Elicitation metadata structure
 */
export interface ElicitationMetadata {
  schema: Record<string, unknown>;
  prompt: string;
  serverName: string;
  context?: Record<string, unknown>;
}

/**
 * Extract elicitation metadata from approval event
 */
export function getElicitationMetadata(
  event: ElicitationApprovalEvent
): ElicitationMetadata | null {
  const metadata = event.metadata;

  if (
    !metadata ||
    !metadata.schema ||
    !metadata.prompt ||
    !metadata.serverName
  ) {
    return null;
  }

  return {
    schema: metadata.schema as Record<string, unknown>,
    prompt: metadata.prompt as string,
    serverName: metadata.serverName as string,
    context: metadata.context as Record<string, unknown> | undefined,
  };
}
